import { QrScanner } from '@/components/QrScanner';
import { PlatformBrand } from '@/components/passport/PlatformBrand';
import { getPassportIcon } from '@/components/passport/icon-map';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiFetch } from '@/lib/api';
import { usePassportStore } from '@/stores/passport-store';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type {
  PassportActivityDto,
  PassportScanOutput as PassportScanOutputType,
} from '@tunodo/ticketing-shared';
import { PassportScanOutput, SpeakerShortCodeSchema } from '@tunodo/ticketing-shared';
import { ArrowLeft, CheckCircle2, KeyRound, Sparkles, XCircle } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/passport/scan/$eventId')({
  beforeLoad: () => {
    const { user } = usePassportStore.getState();
    if (!user) {
      throw redirect({ to: '/passport' });
    }
  },
  component: PassportScanPage,
});

const uuidSchema = z.string().uuid();

type OverlayState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; activity: PassportActivityDto | null }
  | { kind: 'duplicate' }
  | { kind: 'invalid' };

function extractToken(decoded: string): string | null {
  if (uuidSchema.safeParse(decoded).success) return decoded;
  try {
    const url = new URL(decoded);
    const token = url.searchParams.get('token');
    if (token && uuidSchema.safeParse(token).success) return token;
  } catch {
    // not a URL
  }
  return null;
}

function PassportScanPage() {
  const user = usePassportStore((s) => s.user)!;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { eventId } = Route.useParams();
  const [overlay, setOverlay] = useState<OverlayState>({ kind: 'idle' });
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  const isPaused = overlay.kind !== 'idle' || codeDialogOpen;

  useEffect(() => {
    if (overlay.kind !== 'ok') return;
    const timer = setTimeout(async () => {
      void queryClient.invalidateQueries({
        queryKey: ['passport', user.userId, 'events', eventId],
      });
      await navigate({ to: '/passport/events/$eventId', params: { eventId } });
    }, 2500);
    return () => clearTimeout(timer);
  }, [overlay, queryClient, user.userId, navigate, eventId]);

  const submitScan = useCallback(
    async (payload: { token: string } | { shortCode: string }) => {
      setOverlay({ kind: 'loading' });
      try {
        const result = await apiFetch<PassportScanOutputType>('/api/passport/scan', {
          method: 'POST',
          body: JSON.stringify({ userId: user.userId, ...payload }),
          schema: PassportScanOutput,
        });

        if (result.result === 'OK') {
          setOverlay({ kind: 'ok', activity: result.activity });
        } else if (result.result === 'DUPLICATE') {
          setOverlay({ kind: 'duplicate' });
        } else {
          setOverlay({ kind: 'invalid' });
        }
      } catch {
        setOverlay({ kind: 'invalid' });
      }
    },
    [user.userId],
  );

  const handleDecode = useCallback(
    async (decoded: string) => {
      const token = extractToken(decoded);
      if (!token) {
        setOverlay({ kind: 'invalid' });
        return;
      }
      await submitScan({ token });
    },
    [submitScan],
  );

  function dismissOverlay() {
    setOverlay({ kind: 'idle' });
  }

  function openCodeDialog() {
    setCodeInput('');
    setCodeError(null);
    setCodeDialogOpen(true);
  }

  async function handleSubmitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const normalized = codeInput.trim().toUpperCase();
    const parsed = SpeakerShortCodeSchema.safeParse(normalized);
    if (!parsed.success) {
      setCodeError('El código debe tener 4 caracteres (letras o números).');
      return;
    }
    setCodeError(null);
    setCodeDialogOpen(false);
    await submitScan({ shortCode: parsed.data });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center gap-3 px-4 py-3 text-white shrink-0 bg-passport-cover">
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:text-white hover:bg-white/20 p-0 h-auto"
          onClick={() => navigate({ to: '/passport/events/$eventId', params: { eventId } })}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-base font-semibold">Escanear ponencia</h1>
        <PlatformBrand variant="dark" className="ml-auto" />
      </header>

      <div className="shrink-0 px-4 py-3 text-center">
        <Button
          type="button"
          variant="outline"
          onClick={openCodeDialog}
          className="w-full max-w-xs mx-auto"
        >
          <KeyRound className="h-4 w-4 mr-2" />
          Código de Ponente
        </Button>
      </div>

      <div className="flex flex-1 min-h-0 items-center justify-center bg-black overflow-hidden">
        <QrScanner onDecode={handleDecode} paused={isPaused} />
      </div>

      <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Código de Ponente</DialogTitle>
            <DialogDescription>
              Escribe el código de 4 caracteres que está mostrando el ponente. No importa si lo
              escribes en mayúsculas o minúsculas.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCode} className="space-y-3">
            <Input
              autoFocus
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value.toUpperCase());
                if (codeError) setCodeError(null);
              }}
              maxLength={4}
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="EJ. K7P2"
              className="text-center text-2xl tracking-[0.5em] font-mono uppercase"
              aria-invalid={codeError ? true : undefined}
            />
            {codeError && <p className="text-sm text-red-600 text-center">{codeError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCodeDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={codeInput.trim().length === 0}>
                Validar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {overlay.kind === 'loading' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
          <p className="text-lg font-medium">Validando…</p>
        </div>
      )}

      {overlay.kind === 'ok' &&
        (() => {
          const ActivityIcon = getPassportIcon(overlay.activity?.icon ?? null);
          return (
            <div
              className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 p-6"
              style={{ backgroundColor: 'var(--passport-page)' }}
            >
              <div
                className="animate-stamp flex h-28 w-28 items-center justify-center rounded-full border-4"
                style={{
                  borderColor: 'var(--passport-ink-green)',
                  backgroundColor: 'rgba(15,110,86,0.1)',
                }}
              >
                {ActivityIcon ? (
                  <ActivityIcon
                    className="h-14 w-14"
                    style={{ color: 'var(--passport-ink-green)' }}
                    strokeWidth={2.2}
                  />
                ) : (
                  <CheckCircle2
                    className="h-14 w-14"
                    style={{ color: 'var(--passport-ink-green)' }}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" style={{ color: 'var(--passport-gold)' }} />
                <p
                  className="font-passport text-2xl font-bold"
                  style={{ color: 'var(--passport-ink-green)' }}
                >
                  ¡Sello obtenido!
                </p>
                <Sparkles className="h-4 w-4" style={{ color: 'var(--passport-gold)' }} />
              </div>
              {overlay.activity && (
                <div className="flex flex-col items-center gap-1 text-center">
                  <p
                    className="font-passport text-lg font-semibold"
                    style={{ color: 'var(--passport-cover)' }}
                  >
                    {overlay.activity.title}
                  </p>
                  {overlay.activity.speakerName && (
                    <p
                      className="font-passport text-sm italic"
                      style={{ color: 'var(--passport-ink-gray)' }}
                    >
                      Ponente: {overlay.activity.speakerName}
                    </p>
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground">Redirigiendo a tu pasaporte…</p>
            </div>
          );
        })()}

      {overlay.kind === 'duplicate' && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background p-6">
          <XCircle className="h-20 w-20 text-yellow-500" />
          <p className="text-xl font-bold text-yellow-700">Ya escaneaste esta ponencia</p>
          <Button
            onClick={dismissOverlay}
            style={{ backgroundColor: '#522B9A' }}
            className="text-white"
          >
            Continuar
          </Button>
        </div>
      )}

      {overlay.kind === 'invalid' && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background p-6">
          <XCircle className="h-20 w-20 text-red-500" />
          <p className="text-xl font-bold text-red-700">QR no reconocido</p>
          <Button
            onClick={dismissOverlay}
            style={{ backgroundColor: '#522B9A' }}
            className="text-white"
          >
            Continuar
          </Button>
        </div>
      )}
    </div>
  );
}
